import { streamService } from "@/features/chat/services/streamService";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useTranslation } from "react-i18next";
import type { Channel as StreamChannelType, UserResponse } from "stream-chat";
import { useChatContext } from "stream-chat-react";
import {
  Building2,
  Check,
  Search,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import { formatLastSeen } from "@/shared/utils/formatters";

const MAX_GROUP_SIZE = 99;

interface Props {
  mode: "direct" | "group";
  onClose: () => void;
  onChannelCreated: (channel: StreamChannelType) => void;
}

export const UserListModal = ({ mode, onClose, onChannelCreated }: Props) => {
  const { t } = useTranslation();
  const { client: chatClient } = useChatContext();

  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [searchBatch, setSearchBatch] = useState("");
  const [groupName, setGroupName] = useState("");
  const fetchRequestRef = useRef(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const debouncedSearchCompany = useDebounce(searchCompany, 300);
  const debouncedSearchBatch = useDebounce(searchBatch, 300);
  const [selectedUsers, setSelectedUsers] = useState<UserResponse[]>([]);
  const [creating, setCreating] = useState(false);

  const allVisibleSelected =
    mode === "group" &&
    users.length > 0 &&
    users.every((u) => selectedUsers.some((s) => s.id === u.id));

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(users.map((u) => u.id));
      setSelectedUsers(selectedUsers.filter((u) => !visibleIds.has(u.id)));
    } else {
      const currentIds = new Set(selectedUsers.map((u) => u.id));
      const newUsers = users.filter((u) => !currentIds.has(u.id));
      const combined = [...selectedUsers, ...newUsers];
      if (combined.length > MAX_GROUP_SIZE) {
        alert(t("chat.group_limit_message", { max: MAX_GROUP_SIZE }));
        return;
      }
      setSelectedUsers(combined);
    }
  };

  const handleClearAll = () => {
    setSelectedUsers([]);
  };


  const fetchUsers = useCallback(
    async (nameQuery = "", companyQuery = "", batchQuery = "") => {
      if (!chatClient) return;
      const requestId = ++fetchRequestRef.current;
      setLoading(true);
      try {
        let filtered = (await streamService.getContacts(nameQuery)).filter((u) => u.id !== chatClient.userID);

        if (companyQuery.trim()) {
          const q = companyQuery.trim().toLowerCase();
          filtered = filtered.filter((u: any) => {
            const cn = (u.company_name || u.company || "").toLowerCase();
            return cn.includes(q);
          });
        }

        if (companyQuery.trim() && batchQuery.trim()) {
          const bq = batchQuery.trim().toLowerCase();
          filtered = filtered.filter((u: any) => {
            const bn = (u.batch_no || "").toLowerCase();
            return bn.includes(bq);
          });
        }

        if (requestId !== fetchRequestRef.current) return;
        setUsers(filtered);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        if (requestId === fetchRequestRef.current) setLoading(false);
      }
    },
    [chatClient]
  );

  useEffect(() => {
    fetchUsers(debouncedSearchTerm, debouncedSearchCompany, debouncedSearchBatch);
  }, [fetchUsers, debouncedSearchTerm, debouncedSearchCompany, debouncedSearchBatch]);

  const startDirectChat = async (user: UserResponse) => {
    if (!chatClient?.userID || creating) return;
    setCreating(true);
    try {
      const channelId = await streamService.createChannel([user.id.toString()]);
      const channel = chatClient.channel("messaging", channelId);
      await channel.watch();
      onChannelCreated(channel);
    } catch (error: any) {
      console.error("Failed to start chat:", error?.message || error);
      window.alert(`Failed to start chat: ${error?.message || "Unknown error"}`);
    } finally {
      setCreating(false);
    }
  };

  const createGroupChannel = async () => {
    if (!chatClient?.userID || creating || selectedUsers.length < 2) return;
    setCreating(true);
    try {
      const memberIds = [chatClient.userID, ...selectedUsers.map((u) => u.id.toString())];
      const finalName =
        groupName.trim() ||
        `Group: ${selectedUsers
          .map((u) => u.name || u.id)
          .slice(0, 2)
          .join(", ")}`;

      const channelId = await streamService.createChannel(memberIds.filter((id) => id !== chatClient.userID), finalName);
      const channel = chatClient.channel("messaging", channelId);
      await channel.watch();
      onChannelCreated(channel);
    } catch (error: any) {
      console.error("Failed to create group:", error?.message || error);
      alert(`Failed to create group: ${error?.message || "Please try again."}`);
    } finally {
      setCreating(false);
    }
  };

  const toggleUserSelection = (user: UserResponse) => {
    const isSelected = selectedUsers.some((u) => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      if (selectedUsers.length >= MAX_GROUP_SIZE) {
        alert(t("chat.group_limit_message", { max: MAX_GROUP_SIZE }));
        return;
      }
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const isDirect = mode === "direct";
  const modalTitle = isDirect ? t("chat.new_message") : t("chat.new_group");
  const modalSubtitle = isDirect
    ? t("chat.direct")
    : t("chat.selected_count", { count: selectedUsers.length });

  // Group users by company_name, sorted A-Z within each group
  const groupedSections = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      const nameA = (a.name || a.id).toLowerCase();
      const nameB = (b.name || b.id).toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const groups: Record<string, UserResponse[]> = {};
    for (const user of sorted) {
      const key = ((user as any).company_name || (user as any).company || "").trim() || "Others";
      if (!groups[key]) groups[key] = [];
      groups[key].push(user);
    }

    return Object.entries(groups)
      .sort(([a], [b]) => {
        if (a === "Others") return 1;
        if (b === "Others") return -1;
        return a.localeCompare(b);
      })
      .map(([title, data]) => ({ title, data }));
  }, [users]);

  return (
    <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="glass bg-white w-full sm:w-[520px] sm:max-h-[84vh] max-h-[92vh] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl overflow-hidden border border-white/70">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 px-4 py-3.5 flex-shrink-0 border-b border-slate-200/80 bg-white/80">
          {/* Mode icon */}
          <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
            {isDirect ? (
              <UserPlus className="w-5 h-5 text-indigo-600" />
            ) : (
              <Users className="w-5 h-5 text-indigo-600" />
            )}
          </div>

          {/* Title + subtitle */}
          <div className="flex-1 min-w-0">
            <h2 className="text-slate-900 font-semibold text-base leading-tight">{modalTitle}</h2>
            <p className="text-slate-400 text-xs mt-0.5">{modalSubtitle}</p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-4.5 h-4.5 text-slate-500" />
          </button>
        </div>

        {/* ── Group: selected chips + name input ── */}
        {!isDirect && selectedUsers.length > 0 && (
          <div className="border-b border-slate-200/80 bg-white/70 flex-shrink-0">
            {/* Selected user chips */}
            <div className="flex gap-2 px-4 pt-3 pb-2 overflow-x-auto hp-channel-scroll">
              {selectedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-100 rounded-full pl-1 pr-2 py-1 flex-shrink-0"
                >
                  <UserAvatar name={user.name} id={user.id} image={user.image} size={22} />
                  <span className="text-xs text-indigo-800 font-medium max-w-[88px] truncate">
                    {user.name || user.id}
                  </span>
                  <button
                    onClick={() => toggleUserSelection(user)}
                    className="ml-0.5 w-4 h-4 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 flex items-center justify-center flex-shrink-0 transition-colors"
                    aria-label={`Remove ${user.name || user.id}`}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Group name input */}
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 bg-slate-100/80 border border-transparent rounded-xl px-3 h-10 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white focus-within:border-indigo-100">
                <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder={t("chat.enter_group_name")}
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Search fields ── */}
        <div className="px-4 py-3 space-y-2 border-b border-slate-200/80 bg-white/70 flex-shrink-0">
          {/* Name search */}
          <div className="flex items-center gap-2 bg-slate-100/80 rounded-xl px-3 h-10 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white">
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder={t("chat.search_users")}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
              autoFocus
            />
            {searchTerm && (
              <button
                onClick={() => { setSearchTerm(""); }}
                className="w-6 h-6 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/70 flex items-center justify-center transition-colors"
                aria-label="Clear name search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Company search */}
          <div className="flex items-center gap-2 bg-slate-100/80 rounded-xl px-3 h-10 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white">
            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder={t("chat.search_company")}
              value={searchCompany}
              onChange={(e) => {
                setSearchCompany(e.target.value);
                if (!e.target.value.trim()) {
                  setSearchBatch("");
                }
              }}
              className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
            {searchCompany && (
              <button
                onClick={() => { setSearchCompany(""); setSearchBatch(""); }}
                className="w-6 h-6 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/70 flex items-center justify-center transition-colors"
                aria-label="Clear company search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Batch search — only when company is filtered */}
          {searchCompany.trim() && (
            <div className="flex items-center gap-2 bg-slate-100/80 rounded-xl px-3 h-10 transition-shadow focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="text"
                placeholder={t("chat.search_batch")}
                value={searchBatch}
                onChange={(e) => {
                  setSearchBatch(e.target.value);
                }}
                className="flex-1 min-w-0 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400"
              />
              {searchBatch && (
                <button
                  onClick={() => { setSearchBatch(""); }}
                  className="w-6 h-6 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200/70 flex items-center justify-center transition-colors"
                  aria-label="Clear batch search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Select All / Clear All (group mode) ── */}
        {mode === "group" && users.length > 0 && !loading && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 bg-slate-50/80 border-b border-slate-200/80 flex-shrink-0">
            <div className="flex items-center gap-1 bg-white rounded-lg p-0.5 border border-slate-200/70">
              <button
                onClick={handleSelectAll}
                className="text-xs font-medium text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 rounded-md px-2.5 py-1.5 transition-colors"
              >
                {allVisibleSelected ? t("chat.deselect_all") : t("chat.select_all")}
              </button>
              <button
                onClick={handleClearAll}
                disabled={selectedUsers.length === 0}
                className="text-xs font-medium text-slate-500 hover:bg-slate-100 active:bg-slate-200 rounded-md px-2.5 py-1.5 transition-colors disabled:text-slate-300 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                {t("chat.clear_all")}
              </button>
            </div>
            <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
              {t("chat.selected_count", { count: selectedUsers.length })}
            </span>
          </div>
        )}

        {/* ── User list ── */}
        <div className="flex-1 overflow-y-auto hp-channel-scroll bg-white/60">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-8 h-8 rounded-full border-2 border-indigo-100 border-t-indigo-600 animate-spin" />
              <p className="text-sm text-slate-400">{t("chat.loading")}</p>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-slate-300" />
              </div>
              <p className="font-medium text-slate-500 text-sm">{t("chat.no_users_found")}</p>
            </div>
          ) : (
            groupedSections.map(({ title, data }) => (
              <div key={title}>
                {/* Section header */}
                <div className="px-4 py-1.5 bg-white/85 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-10">
                  <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                    {title}
                  </span>
                </div>

                {data.map((user) => {
                  const isSelected = selectedUsers.some((u) => u.id === user.id);
                  const batchNo = (user as any).batch_no;
                  const visaType = (user as any).visa_type_descr || "";
                  const lastSeen = !user.online
                    ? formatLastSeen((user as any).last_active)
                    : "";

                  return (
                    <button
                      key={user.id}
                      onClick={() =>
                        mode === "group" ? toggleUserSelection(user) : startDirectChat(user)
                      }
                      disabled={creating}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 md:py-3 border-b border-slate-50 transition-colors disabled:opacity-50 text-left ${
                        isSelected
                          ? "bg-indigo-50/80"
                          : "hover:bg-slate-50 active:bg-slate-100"
                      }`}
                    >
                      {/* Avatar with online dot */}
                      <div className="relative flex-shrink-0">
                        <UserAvatar name={user.name} id={user.id} image={user.image} size={44} />
                        {user.online && (
                          <span className="absolute bottom-0 right-0 block w-3 h-3 rounded-full bg-green-400 border-2 border-white" />
                        )}
                      </div>

                      {/* Name + badges */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-semibold truncate text-sm ${isSelected ? "text-indigo-900" : "text-slate-900"}`}>
                          {user.name || user.id}
                        </p>
                        {(batchNo || visaType) && (
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {batchNo && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-50 text-amber-700">
                                {t("profile.batch_no")} {batchNo}
                              </span>
                            )}
                            {visaType && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-purple-50 text-purple-700 truncate max-w-[140px]"
                                title={visaType}
                              >
                                {visaType}
                              </span>
                            )}
                          </div>
                        )}
                        {lastSeen && (
                          <p className="text-[11px] text-slate-400 mt-0.5 truncate">{lastSeen}</p>
                        )}
                      </div>

                      {/* Group checkbox */}
                      {mode === "group" && (
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? "bg-indigo-600 border-indigo-600 scale-110 shadow-sm"
                              : "border-slate-300"
                          }`}
                        >
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* ── Group: sticky create button ── */}
        {mode === "group" && selectedUsers.length >= 2 && (
          <div className="px-4 py-3 border-t border-slate-200/80 bg-white/85 flex-shrink-0">
            <button
              onClick={createGroupChannel}
              disabled={creating}
              className="w-full h-11 flex items-center justify-center gap-2 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors shadow-sm text-sm"
            >
              <Users className="w-4 h-4" />
              {creating
                ? t("chat.creating")
                : `${t("common.create")} (${selectedUsers.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
